<template>
  <div class="live-list">
    <header class="live-list-title">{{ $t('home.liveNow') }}</header>
    <template v-if="liveListData.length">
      <ul class="live-items" v-infinite-scroll="fetchLiveList" :infinite-scroll-disabled="disabled">
        <li class="live-item" v-for="(item, index) in liveList" :key="index" @click="onClickLiveItem(item?.room_info.room_id)">
          <live-card :card-data="item"></live-card>
        </li>
      </ul>
      <div class="live-items-bottom">
        <div v-if="loading" class="loading">
          <span class="loading-icon"></span>
          <span class="loading-txt">{{ $t('home.loadMore') }}</span>
        </div>
        <div v-if="error" class="load-error">
          <p class="load-error-txt">{{ $t('home.refreshFailed') }}</p>
          <button class="reload-btn" @click="fetchLiveList">{{ $t('home.refresh') }}</button>
        </div>
        <div v-if="!hasMore || !firstHasMore" class="no-more">{{ $t('home.noMore') }}</div>
      </div>
    </template>
    <template v-else>
      <div class="live-list-empty">
        <img class="empty-img" src="@/assets/live-list-empty.svg">
        <p class="empty-txt">{{ $t('home.refreshFailed') }}</p>
        <button class="reload-btn" @click="fetchLiveList">{{ $t('home.refresh') }}</button>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { toRefs, computed } from 'vue';
import {ElInfiniteScroll as vInfiniteScroll} from 'element-plus';
import type {ILiveCard} from '@/service/home/types';
import { LogReporter } from '@/common/log-report';
import {useLiveList}  from './use-live-list';
import LiveCard from './live-card.vue';

const props = defineProps<{liveListData: ILiveCard[], firstHasMore: boolean}>();
const { liveListData, firstHasMore } = toRefs(props);
const emit = defineEmits(['pausePlayer']);

const {
  loading,
  error,
  fetchLiveList,
  liveList,
  hasMore
} = useLiveList(liveListData.value);

const spmId = LogReporter.getSpmId();


const disabled = computed(() => loading.value || !hasMore.value || error.value || !firstHasMore.value);

const onClickLiveItem = (id:number) => {
  // window.open(location.href + (location.href.endsWith('/')? id : `/${id}`));
  emit('pausePlayer');
};

</script>

<style lang="less" scoped>

@import '@/styles/var.less';
@import '@/styles/util.less';
@import '@/styles/polyfill/gap.less';

.live-list {
  min-width: 512px;

  .live-list-title {
    font-size: 18px;
    color: #333;
    letter-spacing: 0;
    line-height: 38px;
    font-weight: 700;
  }

  .live-items {
    margin-top: 16px;
    display: grid;
    grid-template-rows: auto auto;
    grid-auto-rows: auto;
    row-gap: 24px;
    // grid-template-columns: repeat(6, 1fr);
    // column-gap: 24px;
  }

  // >= 2318
  @media (min-width: @screen-xxl) {
    padding: 0 48px;
    max-width: 1950px;
    margin: 0 auto;

    .live-items {
      grid-template-columns: repeat(6, 1fr);
      column-gap: 24px;
    }
  }

  // [1920, 2318)
  @media (max-width: @screen-xxl) and (min-width: @screen-xl) {
    padding: 0 40px;

    .live-items {
      grid-template-columns: repeat(5, 1fr);
      column-gap: 24px;
    }
  }

  // [1280, 1920)
  @media (max-width: @screen-l-max) and (min-width: @screen-m) {
    padding: 0 32px;

    .live-items {
      grid-template-columns: repeat(4, 1fr);
      column-gap: 24px;
    }
  }
  // [888, 1280)
  @media (max-width: @screen-m) and (min-width: @screen-s) {
    padding: 0 24px;

    .live-items {
      grid-template-columns: repeat(3, 1fr);
      column-gap: 16px;
    }
  }

  // < 888
  @media (max-width: @screen-s) {
    padding: 0 16px;

    .live-items {
      grid-template-columns: repeat(2, 1fr);
      column-gap: 16px;
    }
  }

  .live-items-bottom {
    padding: 48px 0;

    .loading {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 75px;

      .loading-icon {
        width: 46px;
        height: 46px;
        background: url('@/assets/icon_loading.gif') no-repeat center center;
        background-size: 100%;
      }

      .loading-txt {
        text-align: center;
        font-size: 14px;
        color: #666;
      }
    }

    .load-error {
      .load-error-txt {
        text-align: center;
        font-size: 14px;
        color: #999;
      }

      .reload-btn {
        margin: 24px auto 0;
        color: #4c93ff;
        border: 1px solid #4c93ff;
        font-weight: 700;
        line-height: 32px;
        font-size: 14px;
        display: block;
        padding: 0 12px;
        border-radius: 4px;
        text-align: center;

        &:hover {
          background: rgba(76, 147, 255, 0.1);
        }
      }
    }

    .no-more {
      font-size: 14px;
      line-height: 18px;
      text-align: center;
      color: #999;
      margin: 32px auto;
    }
  }
}

.live-list-empty {
  padding: 32px 24px;

  .empty-img {
    display: block;
    width: 246px;
    height: 204px;
    margin: 0 auto;
  }

  .empty-txt {
    font-size: 14px;
    color: #999;
    letter-spacing: 0;
    text-align: center;
    line-height: 18px;
    font-weight: 400;
    margin-top: 24px;
  }

  .reload-btn {
    width: 80px;
    height: 36px;
    border: 1px solid rgba(93, 157, 255, 1);
    border-radius: 4px;
    font-size: 14px;
    color: #5d9dff;
    line-height: 36px;
    font-weight: 700;
    text-align: center;
    display: block;
    margin: 30px auto 0;
  }
}

</style>